import { twMerge } from '@panwds/tailwindcss';
import { endOfDay, format, isAfter, isBefore, isSameDay, isSameMonth, isToday, isWithinInterval, startOfDay } from 'date-fns';
import React, { useState } from 'react';
import { getDataTestId } from '../../../utils/utils.js';
import { getCellAlignment, getCellBGColor, getCellMargin, getCellRadius, getCellWidth, getContainerBGColor, getContainerBorder, getContainerRadius, getTextColor } from './day.logic.js';
function getDateRange({ activeDate, endDate, hoverDate, startDate }) {
    if (!startDate)
        return null;
    if (endDate) {
        return { start: startDate, end: endDate };
    }
    if (hoverDate && isAfter(hoverDate, startDate)) {
        return { start: startDate, end: hoverDate };
    }
    if (hoverDate && isBefore(hoverDate, startDate)) {
        return { start: hoverDate, end: startDate };
    }
    if (activeDate && isAfter(activeDate, startDate)) {
        return { start: startDate, end: activeDate };
    }
    if (activeDate && isBefore(activeDate, startDate)) {
        return { start: activeDate, end: startDate };
    }
    /* istanbul ignore next unreachable, but required for typeing */
    return null;
}
export function Day({ activeDate, dataTestId: propsDataTestId, day, hoverDate, index, max, min, onHoverDateChange, onSelectedDateChange, selectedDate, dateRange = {} }) {
    const { end: endDate, start: startDate } = dateRange;
    const isSelected = !!selectedDate && isSameDay(selectedDate, day);
    const isEndDate = !!endDate && isSameDay(endDate, day);
    const isStartDate = !!startDate && isSameDay(startDate, day);
    const isCurrentMonth = isSameMonth(activeDate, day);
    const isDisabled = (min && isBefore(day, startOfDay(min))) || (max && isAfter(day, endOfDay(max)));
    const [focus, setFocus] = useState(false);
    const interval = getDateRange({
        activeDate,
        endDate: endDate && endOfDay(endDate),
        hoverDate,
        startDate: startDate && startOfDay(startDate)
    });
    const insideRange = interval && isWithinInterval(day, interval);
    const isIntervalEndDate = !!interval?.end && isSameDay(interval.end, day);
    const isIntervalStartDate = !!interval?.start && isSameDay(interval.start, day);
    const todayDateClassName = twMerge('twds-w-2 twds-border twds-border-solid twds-rounded-full', !(isDisabled || !isCurrentMonth) && (isSelected || isEndDate || isStartDate)
        ? 'twds-border-white'
        : 'twds-border-blue-600', (isDisabled || !isCurrentMonth) && 'twds-border-blue-300');
    const date = format(day, 'd');
    const isHoverStartDate = isIntervalStartDate || (!!insideRange && index === 0);
    const isHoverEndDate = isIntervalEndDate || (!!insideRange && index === 6);
    const dataTestId = getDataTestId('day', propsDataTestId);
    return (React.createElement("div", { "data-testid": dataTestId, role: "cell", className: twMerge('twds-flex twds-items-center', isDisabled && 'twds-cursor-not-allowed', getCellWidth({
            isHoverStartDate,
            isHoverEndDate,
            isInRange: !!insideRange
        }), getCellAlignment({
            isHoverStartDate,
            isHoverEndDate,
            isInRange: !!insideRange
        }), getCellMargin({
            isHoverStartDate,
            isHoverEndDate,
            isInRange: !!insideRange
        }), getCellBGColor({
            isInRange: !!insideRange
        }), getCellRadius({
            isHoverStartDate,
            isHoverEndDate,
            isStartDate: isIntervalStartDate && index === 6,
            isEndDate: isIntervalEndDate && index === 0
        })) },
        React.createElement("div", { "data-testid": getDataTestId('container', dataTestId), className: twMerge('twds-flex twds-justify-center twds-items-center  twds-w-7 twds-h-7', isDisabled && 'twds-cursor-not-allowed', getContainerBGColor({
                isStartDate: isStartDate,
                isEndDate: isEndDate,
                isSelected,
                focus
            }), getContainerRadius({
                isStartDate: isStartDate,
                isEndDate: isEndDate,
                isSelected,
                focus
            }), getContainerBorder({
                isStartDate: isStartDate,
                isEndDate: isEndDate,
                isSelected,
                focus
            })) },
            React.createElement("button", { type: "button", "aria-hidden": isCurrentMonth ? undefined : true, "aria-selected": isSelected || isEndDate || isStartDate, className: twMerge('twds-flex twds-justify-center twds-items-center twds-flex-col twds-w-5 twds-h-5 twds-border-none twds-bg-transparent twds-outline-none', isDisabled ? 'twds-cursor-not-allowed' : 'hover:twds-cursor-pointer', getTextColor({
                    isStartDate: isStartDate,
                    isEndDate: isEndDate,
                    isSelected,
                    isCurrentMonth,
                    isDisabled
                })), "data-date": isCurrentMonth ? date : undefined, "data-testid": `${dataTestId}-button`, disabled: isDisabled, onClick: () => {
                    onSelectedDateChange(day);
                }, onMouseEnter: () => {
                    onHoverDateChange?.(day);
                }, onFocus: () => {
                    setFocus(true);
                }, onBlur: () => setFocus(false), tabIndex: focus ? 0 : -1 },
                date,
                isToday(day) && React.createElement("span", { className: todayDateClassName, "data-testid": "today" })))));
}
//# sourceMappingURL=Day.js.map